import { useEffect, useState } from 'react';
import { ConsentParams } from './useMcpConsent';

const API_BASE = process.env.GQL_URI ?? '';

interface ClientDetails {
  client_name: string | null;
  logo_uri: string | null;
  redirect_hostname: string;
  is_local: boolean;
  permissions: string[];
}

interface ClientState {
  requestKey: string;
  details?: ClientDetails;
  error?: string;
}

export function useMcpClient({
  clientId,
  redirectUri,
  state,
  codeChallenge,
  codeChallengeMethod,
  scope,
}: ConsentParams) {
  // Include every consent parameter so stale results can never enable approval for a different request.
  const requestKey = JSON.stringify({
    client_id: clientId,
    redirect_uri: redirectUri,
    state,
    code_challenge: codeChallenge,
    code_challenge_method: codeChallengeMethod,
    ...(scope && { scope }),
  });
  const [result, setResult] = useState<ClientState>();

  useEffect(() => {
    let cancelled = false;
    const loadClient = async () => {
      try {
        const response = await fetch(`${API_BASE}/mcp/consent-details`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: requestKey,
        });
        const details = await response.json();
        if (!response.ok) {
          throw new Error(
            details.error_description ??
              'Unable to load consent details. Please restart the connection from your MCP client.',
          );
        }
        if (
          !details ||
          typeof details.redirect_hostname !== 'string' ||
          !details.redirect_hostname ||
          typeof details.is_local !== 'boolean' ||
          !Array.isArray(details.permissions) ||
          !details.permissions.length ||
          !details.permissions.every((permission: unknown) => typeof permission === 'string') ||
          (details.client_name !== null && typeof details.client_name !== 'string') ||
          (details.logo_uri !== null && typeof details.logo_uri !== 'string')
        )
          throw new Error('Invalid consent details. Please restart the connection from your MCP client.');
        if (!cancelled) setResult({ requestKey, details });
      } catch (error) {
        if (!cancelled) {
          setResult({
            requestKey,
            error: error instanceof Error ? error.message : 'Unable to load consent details.',
          });
        }
      }
    };

    void loadClient();
    return () => {
      cancelled = true;
    };
  }, [requestKey]);

  const current = result?.requestKey === requestKey ? result : undefined;
  return { client: current?.details, loading: !current, error: current?.error };
}
